/**
 * Every app this window can see: the World's and this project's (RFC 0295 S2).
 *
 * Reads the World pointer and merges its catalog with the project's, exactly as
 * `useWorldSkills` follows the same pointer for skills. Reactive on both, so
 * installing anywhere on the deployment refreshes the list on its own.
 *
 * WHY A HOOK AND NOT A CALL. `projectApps(projectDoc)` is one argument and
 * this is two, and the second one comes from a pointer outside React's tree. A
 * component that reached for the World itself would have to know where the
 * pointer lives, which is the coupling `useWorldSkills` already avoids.
 *
 * THE PROJECT SHADOWS THE WORLD, decided in `installedApps` rather than here,
 * so the cli and the agent tools get the same answer this does. A merge rule
 * living in a React hook would be a rule only the renderer obeys.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { type AppSummary, installedApps } from '@mod/core/model'
import { useMemo } from 'react'
import { useDocument } from './repo/useDocument'
import { readWorldPointer } from './world'

export function useInstalledApps(projectDoc: unknown): AppSummary[] {
  const worldId = readWorldPointer() ?? undefined
  const [worldDoc] = useDocument(worldId as AnyDocumentId | undefined)
  return useMemo(
    () => installedApps(worldDoc, projectDoc),
    [worldDoc, projectDoc],
  )
}
